/**
 * The console's scope generation — the client half of cross-tab scope safety (plan-014 A4, ED-12,
 * design §27.6.4, FR12-AUTH-11).
 *
 * Every switch of organization or workspace rotates the auth_session and draws a new
 * `scope_generation` (migration 0078). The cookie is shared by every tab of the browser, so a tab
 * still showing the previous organization would otherwise read and write in the new one. The server
 * refuses a cookie request to a scoped route whose `X-WeTalk-Scope-Generation` is not current
 * (`409 scope_changed`, `apps/api/src/http/scope-generation.ts`); this store is what the console
 * sends, and what it does when it learns the scope moved.
 *
 * TWO WAYS THE GENERATION MOVES, and they are deliberately different:
 *
 *   · `adopt(n)` — this tab READ or CAUSED the scope: an auth_session body (`GET /v1/session`, a
 *     sign-in, its own switch). Requests carry `n` from now on.
 *   · `noteLeft(n)` — this tab LEARNED the scope moved elsewhere: another tab's broadcast, or a
 *     `409 scope_changed` refusal. The store does NOT adopt `n`: a page of the previous organization
 *     must never be answered from the new one just because it retried with the newer number. It goes
 *     STALE instead — the gateway refuses every scoped request locally until the auth_session is
 *     re-read (which adopts), and the scope guard clears what the old scope left behind.
 *
 * The store knows nothing about React, queries or the network: the gateway (`../api/api-gateway.ts`)
 * and the scope guard (`./guard.tsx`) subscribe to it.
 */

/** What changed, for a listener. `previous` is the generation the tab was on, `null` before any. */
export type ScopeGenerationEvent =
  | { readonly kind: "adopted"; readonly generation: number; readonly previous: number | null }
  | { readonly kind: "left"; readonly generation: number | null; readonly previous: number | null };

export interface ScopeGeneration {
  /** The generation requests carry, or `null` before any auth_session body was read. */
  current(): number | null;
  /** True after the scope moved elsewhere and before this tab re-read its auth_session. */
  isStale(): boolean;
  /** This tab read or caused the auth_session at `generation`. */
  adopt(generation: number): void;
  /**
   * The scope moved elsewhere — to `generation` when the refusal or broadcast named it, `null` when
   * it did not. A broadcast of the generation this tab already holds (its own switch, echoed) is not
   * a move and is ignored.
   */
  noteLeft(generation: number | null): void;
  /** Signed out: nothing is current and nothing is stale. Not an event. */
  forget(): void;
  onChange(listener: (event: ScopeGenerationEvent) => void): () => void;
}

/** A generation is a positive whole number (0078's sequence); anything else is a wire fault. */
export function isScopeGeneration(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1;
}

export function createScopeGeneration(): ScopeGeneration {
  let current: number | null = null;
  let stale = false;
  const listener = new Set<(event: ScopeGenerationEvent) => void>();

  const emit = (event: ScopeGenerationEvent): void => {
    for (const one of [...listener]) one(event);
  };

  return {
    current: () => current,
    isStale: () => stale,
    adopt(generation) {
      if (!isScopeGeneration(generation)) {
        throw new Error(`scope generation ${String(generation)} is not a positive whole number`);
      }
      if (generation === current && !stale) return;
      const previous = current;
      current = generation;
      stale = false;
      emit({ kind: "adopted", generation, previous });
    },
    noteLeft(generation) {
      if (stale) return;
      if (generation !== null && generation === current) return;
      stale = true;
      emit({ kind: "left", generation, previous: current });
    },
    forget() {
      current = null;
      stale = false;
    },
    onChange(one) {
      listener.add(one);
      return () => {
        listener.delete(one);
      };
    },
  };
}
